import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import utc from "dayjs/plugin/utc";

import type { Period } from "@/shared/domain/metrics";

dayjs.extend(utc);
dayjs.extend(isoWeek);
dayjs.extend(quarterOfYear);

const NS_PER_MS = 1_000_000n;

/**
 * Every unit {@link TRUNC_UNIT} names.
 *
 * `startOf` is overloaded once per plugin — core takes `OpUnitType`,
 * quarterOfYear adds `QUnitType`, isoWeek adds `'isoWeek'` — and no single
 * overload accepts all five, so the call site casts to the core one. Each
 * value is individually valid given the plugins extended above; the cast only
 * bridges an overload set TypeScript will not union.
 */
type TruncUnit = dayjs.OpUnitType | dayjs.QUnitType | "isoWeek";

/**
 * The dayjs unit each period truncates to, chosen to reproduce the Postgres
 * `date_trunc` the aggregate query buckets with (`_PERIOD_MAP` in
 * `metrics_repo.py`).
 *
 * `isoWeek` rather than `week` is the one that matters: `date_trunc('week')`
 * starts on Monday and dayjs's plain `week` starts on Sunday, so the plain
 * unit would put every weekly boundary a day early.
 */
const TRUNC_UNIT: Record<Period, TruncUnit> = {
  daily: "day",
  weekly: "isoWeek",
  monthly: "month",
  quarterly: "quarter",
  yearly: "year",
};

/**
 * How far one period steps, as a dayjs `add` pair — the client-side twin of
 * the backend's `_INTERVAL_MAP`.
 *
 * Calendar units, not a fixed span: months are 28-31 days and years 365-366,
 * so stepping by a measured duration drifts off the boundaries `date_trunc`
 * produces the moment it crosses February or a leap year.
 */
const STEP: Record<Period, { count: number; unit: dayjs.ManipulateType }> = {
  daily: { count: 1, unit: "day" },
  weekly: { count: 1, unit: "week" },
  monthly: { count: 1, unit: "month" },
  quarterly: { count: 3, unit: "month" },
  yearly: { count: 1, unit: "year" },
};

/**
 * Epoch nanoseconds as epoch milliseconds, exactly.
 *
 * `Number(ns)` would not do: a 2026 timestamp in nanoseconds is ~200x
 * `Number.MAX_SAFE_INTEGER` and quantizes to 256ns steps. Dividing while still
 * a bigint lands well inside the safe range, and the sub-millisecond remainder
 * it drops is far below the resolution of a calendar boundary.
 */
function nsToMs(ns: bigint): number {
  return Number(ns / NS_PER_MS);
}

/**
 * The start of the calendar period containing `ns`, in Unix-epoch nanoseconds.
 *
 * This is `date_trunc(<period>, ...)` evaluated in UTC — the same boundary the
 * server's `GROUP BY` lands its buckets on, so an axis built from these agrees
 * with the response rather than approximating it. An instant already sitting
 * on a boundary is returned unchanged.
 */
export function truncateToPeriodNs(ns: bigint, period: Period): bigint {
  const startMs = dayjs
    .utc(nsToMs(ns))
    .startOf(TRUNC_UNIT[period] as dayjs.OpUnitType)
    .valueOf();
  return BigInt(startMs) * NS_PER_MS;
}

/**
 * `ns` moved `count` whole periods, in Unix-epoch nanoseconds. Negative counts
 * step backward.
 *
 * Applied to a boundary from {@link truncateToPeriodNs} the result is the
 * neighbouring boundary, which is what lets an axis be walked outward without
 * measuring any bucket's width.
 */
export function advancePeriodNs(ns: bigint, period: Period, count = 1): bigint {
  const { count: stride, unit } = STEP[period];
  const movedMs = dayjs
    .utc(nsToMs(ns))
    .add(count * stride, unit)
    .valueOf();
  return BigInt(movedMs) * NS_PER_MS;
}
